import { t } from "@lingui/macro";
import * as React from "react";
import { Path, Text as SvgText } from "react-native-svg";

import { useGetClubCurrency } from "@whoosh/features/src/ClubCurrency";

import { Icon, IconProps } from "../../../primitive";
import { DollarAltIcon } from "./DollarAltIcon";
import { DollarExemptIcon } from "./DollarExemptIcon";
import { DollarIcon } from "./DollarIcon";

type CurrencyIconVariant = "default" | "alt" | "exempt";

type CurrencyIconProps = IconProps & {
  variant?: CurrencyIconVariant;
};

const dollarIcons: Record<CurrencyIconVariant, React.FC<IconProps>> = {
  default: DollarIcon,
  alt: DollarAltIcon,
  exempt: DollarExemptIcon,
};

// Club-currency-aware replacement for the Dollar* icons. USD clubs keep the
// original dollar artwork; other currencies draw the club's symbol (e.g. "£")
// in the same box so existing sizing and colors carry over.
export const CurrencyIcon = ({
  variant = "default",
  ...props
}: CurrencyIconProps) => {
  const { currencySymbol } = useGetClubCurrency();

  if (currencySymbol === "$") {
    const DollarVariant = dollarIcons[variant];
    return <DollarVariant {...props} />;
  }

  return (
    <Icon viewBox="0 0 16 16" fill="none" title={t`Currency`} {...props}>
      <SvgText
        x={8}
        y={12.5}
        fontSize={variant === "exempt" ? 12 : 13}
        fontWeight="600"
        textAnchor="middle"
        fill="currentColor"
      >
        {currencySymbol}
      </SvgText>
      {variant === "exempt" ? (
        <>
          <Path
            d="M0 17L17 0"
            stroke="white"
            strokeWidth="4.5"
            strokeLinecap="round"
          />
          <Path
            d="M2 14L14 2"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </>
      ) : null}
    </Icon>
  );
};
